import {Base, Connection, GuideBox, NodeDetails, Shape, TrackingChange} from 'diagram-js/lib/model';
import {
  END_NODE_ID, endEvent,
  exclusiveGatewayType,
  guideBoxPropertyExtensionType,
  nodesDetailsExtensionType, processType, startEvent,
  subProcessType,
  taskType,
  trackingChangeExtensionType
} from '@ispm-shared/modules/bpmn/diagram-element-terms';
import {ConnectionModdleElement, NodeModdleElement} from '@ispm/shared/bpmn-io/bpmn-models';
import {generateCounterName} from '@ispm/shared/bpmn-io/sub-process-consts';
import {get, isEmpty, isNil} from 'lodash';
import {is} from 'bpmn-js/lib/util/ModelUtil';
import {allNodeTypes, NodeType} from '@ispm/shared/bpmn-io/node-type';
import {connection} from '../../../../../../ispm-web/shared/modules/bpmn/index';
import { OverflowDirection } from "@ispm/shared/bpmn-io/node-type";
/**
 * A Utility class responsible for bpmn tree/graph functionalities
 */
export class ModelingUtil {

  public static getTreeChildren(element: Shape, excludeMarkedForDeletedNodes?: boolean,
                                excludeMarkedForDeletedConnections?: boolean, visited?: Set<Shape>): Array<Shape> {
    if (!visited) {
      visited = new Set<Shape>();
    }
    visited.add(element);
    let shapes = [];
    const connections = excludeMarkedForDeletedConnections ? element.outgoing.filter(connection => !this.hasDeletionTrackingChange(connection)) : element.outgoing;
    for (const conn of connections) {
      const target = conn.target;
      if(!excludeMarkedForDeletedNodes){
          shapes.push(target);
      } else {
          if (!this.hasDeletionTrackingChange(element)){
              shapes.push(target);
          }
      }
      if (! visited.has(target)) {
        shapes = [...shapes, ...this.getTreeChildren(target, excludeMarkedForDeletedNodes, excludeMarkedForDeletedNodes, visited)];
      }
    }
    return shapes;
  }

  public static getNodeParentsIds(shape: Shape | Connection): Array<string> {

    const parents = this.getElementType(shape) === connection ? ModelingUtil.getNodeParentsForConnection(shape) : ModelingUtil.getNodeParents(shape);
    return parents !== undefined && parents.length > 0 ? parents.map(parent => parent && parent.id ? parent.id : undefined) : [];
  }

  public static getConnectionIdsFromParentToLeaf(parent: Shape, leaf: Shape) {
    const outgoing = ModelingUtil.getNodeOutgoingConnections(parent).map(connection => connection.id);
    const incoming = ModelingUtil.getNodeIncomingConnections(leaf).map(connection => connection.id);
    const intersection = incoming.filter(connection => outgoing.includes(connection));
    return intersection;
  }


  public static getNodeIncomingConnections(shape: Shape, includeMarkedForDeleted?: boolean): Connection[] {
    if (includeMarkedForDeleted) {
      return shape.incoming ? shape.incoming : [];
    }
    return shape.incoming ? shape.incoming.filter((connection) => !this.hasDeletionTrackingChange(connection)) : [];
  }


  public static getNodeOutgoingConnections(shape: Shape, includeMarkedForDeleted?: boolean): Connection[] {

    if (includeMarkedForDeleted) {
      return shape.outgoing ? shape.outgoing : [];

    }
    return shape.outgoing ? shape.outgoing.filter((connection) => !this.hasDeletionTrackingChange(connection)) : [];
  }

  public static getDescendantNodeById(shape: Shape, nodeId: string): Shape {
    const subtree: Shape[] = ModelingUtil.getTreeChildren(shape);
    return subtree.filter(child => child.id === nodeId)[0];
  }

  public static getNodeIncomingGateway(shape: Shape): Shape {
    const incomingConnection = ModelingUtil.getNodeIncomingConnections(shape)[0];

    const incomingShape = incomingConnection !== undefined && !this.hasDeletionTrackingChange(incomingConnection) ? incomingConnection.source : undefined;

    return this.isGateway(incomingShape) ? incomingShape : undefined;
  }

  public static getNodeOutGoingGateway(shape: Shape): Shape {
    const outgoingConnection = ModelingUtil.getNodeOutgoingConnections(shape)[0];

    const outgoingShape = outgoingConnection !== undefined && !this.hasDeletionTrackingChange(outgoingConnection) ? outgoingConnection.target : undefined;

    return this.isGateway(outgoingShape) ? outgoingShape : undefined;
  }

  public static getNodeParents(shape: Shape): Array<Shape> {
    const nodeType = ModelingUtil.getNodeType(shape);
    if (nodeType === NodeType.treatment || nodeType === NodeType.neighborhood_head || nodeType === NodeType.head) {
      return shape.incoming.map(connection => connection.source);
    } else {
      const gateway = ModelingUtil.getNodeIncomingGateway(shape);
      const parents = gateway !== undefined ? gateway.incoming.map(connection => connection.source) : [];
      return parents;
    }
  }

  public static getNodeParentsForConnection(connection: Connection): Array<Shape> {
    if (this.isGateway(connection.source)) {
      const gateway = connection.source;
      const parents = gateway !== undefined ? gateway.incoming.map(connection => connection.source) : [];
      return parents;
    } else {
      return [connection.source];
    }
  }

  public static hasTargetTreatmentPlanNode(element): boolean {
    if (element.outgoing && element.outgoing.length > 0) {
      const connection = element.outgoing[0];
      return this.getNodeType(connection.target) === NodeType.treatment;
    }
    return false
  }

  public static getNodeAncestors(element): Array<Array<Shape>> {
    return this.getNodeAncestorsRecursion(element, []);
  }

  public static getNodeFirstAncestor(element): Shape {
    const ancestors = ModelingUtil.getNodeAncestors(element);
    const flattedAncestors = ancestors.reduce((acc, val) => acc.concat(val), []);
    if (flattedAncestors && ancestors.length > 0) {
      return flattedAncestors[flattedAncestors.length - 1];
    }
    return element;
  }

  public static getNodeDescendants(shape: Shape, descendantType?: NodeType) {
    const filter = ! isNil(descendantType) ? (node) => this.getNodeType(node) === descendantType : (node) => true;
    return this.getNodeDescendantsRecursive(shape, [], filter);
  }

  public static getNodePathsToRoot(element: Shape) {
    const paths = [];
    this.getPathsToRoot(element, [], paths);
    return paths;
  }

  public static hasLoopBetweenNodes(source: Shape, target: Shape): boolean {
    if (this.isOnTheSameBranch(source, target)) {
      let hasLoop = false;
      if (source && target && source.id !== target.id) {
        const ancestors = ModelingUtil.getElementAncestors(source);
        hasLoop = ancestors.some(shape => shape[0].id === target.id);
      }
      if (hasLoop) {
        return true;
      }
      const isBrothers = this.isBrothers(source, target);
      if (isBrothers) {
        return true;
      }
      const isConnectionToPreviousLayer = this.isConnectionToPreviousLayer(source, target);
      return isConnectionToPreviousLayer;
    }
    return false;
  }

  public static isOnTheSameTreeLayer(source: Shape, target: Shape): boolean {
    const sourceLayer = source && source.businessObject ? source.businessObject.neighborhoodID : undefined;
    const targetLayer = target && target.businessObject ? target.businessObject.neighborhoodID : undefined;
    return sourceLayer && targetLayer ? sourceLayer === targetLayer : true;
  }

  public static isOnTheSameBranch(node1: Shape, node2: Shape) {
    const ancestors1 = ModelingUtil.getElementAncestors(node1);
    const ancestors2 = ModelingUtil.getElementAncestors(node2);
    const flattedAncestors1 = ancestors1.reduce((acc, val) => acc.concat(val), []);
    const flattedAncestors2 = ancestors2.reduce((acc, val) => acc.concat(val), []);
    const ancestors1Ids = flattedAncestors1.map(ans => ans.id);
    const ancestors2Ids = flattedAncestors2.map(ans => ans.id);
    const intersection = ancestors1Ids.find(ans => ancestors2Ids.includes(ans));
    return intersection !== undefined;
  }

  public static isBrothers(source: Shape, target: Shape) {
    const sourceIncomingConnection = this.getNodeIncomingConnections(source);
    const targetIncomingConnections = this.getNodeIncomingConnections(target);
    const parentSource = sourceIncomingConnection && sourceIncomingConnection.length > 0 ? sourceIncomingConnection[0].source : undefined;
    const parentTarget = targetIncomingConnections && targetIncomingConnections.length > 0 ? targetIncomingConnections[0].source : undefined;
    return parentSource && parentTarget ?
      ModelingUtil.isGateway(parentSource) &&
      ModelingUtil.isGateway(parentTarget) &&
      parentSource.id === parentTarget.id
      : false;
  }

  /**
   * return all parent elements include gateways
   * @param element
   */
  public static getElementAncestors(element): Array<Array<Shape>> {
    let shapes = [];
    const incomingConnections = this.getNodeIncomingConnections(element);
    let source;
    for (const conn of incomingConnections) {
      source = conn.source;
      shapes.push(source);
    }
    if (incomingConnections.length > 0) {
      shapes = ModelingUtil.mergeArrays(shapes, this.getElementAncestors(source));
    }
    return shapes;
  }

  /**
   * checks if a node has a sibling (brothers&sisters from the same parent node) node connected to the same target node
   * @param element
   * @return true if it has a connected sibling node, false otherwise
   */
  public static hasTargetConnectedSiblingNodes(element: Shape): boolean {
    // get element parent gateways
    const elementParentGateways: Array<Shape> = ModelingUtil.getParentGateways(element);

    // get all incomings of the found element gateways
    const outgoingConnections = element.outgoing;
    if (outgoingConnections.length > 0) {
      // get outcoming gateway of the element
      const elementChildGateway = outgoingConnections[0].target;
      let result: boolean = false;

      if (elementChildGateway.incoming.length > 1) {
        return true;
      } else {
        // get outcoming gateway of the element
        elementChildGateway.incoming.forEach(incomingConnection => {
          const source = incomingConnection.source;
          if (source.id !== element.id) {
            // get incoming gateways of elements that connected to outcoming gateway
            const sourceParentGateways: Array<Shape> = ModelingUtil.getParentGateways(source);
            if (sourceParentGateways.length > 0) {
              sourceParentGateways.forEach(spg => {
                if (elementParentGateways.some(epg => epg === spg) === true) {
                  // I found a brother!
                  result = true;
                }
              });
            }
          }
        });
        return result;
      }
    }
    return false;
  }

  public static isSelectableShapeType(shape: Shape) {
    const nodeType: NodeType = ModelingUtil.getNodeType(shape);
    return allNodeTypes.includes(nodeType);
  }

  public static getSelectableChildNodes(shape: Shape): Array<Shape> {
    const childElements = shape.outgoing.map(connection => connection.target);
    const selectableChildren = childElements.filter(child => ModelingUtil.isSelectableShapeType(child));
    if (selectableChildren && selectableChildren.length > 0) {
      return selectableChildren;
    }
    if (childElements && childElements.length > 0) {
      return childElements[0].outgoing.map(connection => connection.target);
    }
    return [];
  }

  public static getDirectTreeChildrenNodesIds(shape: Shape): Array<string> {
    const childElements = this.getDirectTreeChildrenNodes(shape);
    return childElements.map(child => child.id);
  }

  public static getDirectTreeChildrenNodes(shape: Shape): Array<Shape> {
    const outgoing = shape.outgoing.filter(connection => this.filterConnectionTrackingChange(connection));
    if (outgoing && outgoing.length > 0) {
      const childElements = outgoing.map(connection => connection.target);
      if (childElements && childElements.length > 0) {
        let directChildren;
        if(childElements.length === 1 && ModelingUtil.getNodeType(childElements[0]) === NodeType.treatment){
          directChildren = childElements.filter(child => !this.hasDeletionTrackingChange(child));
        } else {
          if(ModelingUtil.isGateway(shape)) {
            directChildren = childElements;
          } else {
            directChildren = childElements[0].outgoing.filter(connection => this.filterConnectionTrackingChange(connection)).map(connection => connection.target);
          }
          directChildren = directChildren.filter(child => !this.hasDeletionTrackingChange(child));
        }

        return directChildren;
      }
    }
    return [];
  }

  public static filterConnectionTrackingChange(connection): boolean {
    return !this.hasDeletionTrackingChange(connection) && !connection.preDeletionTrackingChange;
  }

  public static getNodeSiblingNodesIds(shape: Shape) {
    return shape.outgoing.map(connection => connection.target.id);
  }

  public static getGatewayDirectChildren(gateway: Shape) {
    return gateway.outgoing.map(connection => connection.target);
  }

  public static getScriptAndUserTask<E extends Base>(element: Base, type: string,elementRegistry:any): E {
    if(element.children && element.children.length > 0) {
      return element.children.filter((child: Base) => child.type === type)[0];
    }

    const planeElement = elementRegistry._elements[element.id + "_plane"];
    if(planeElement) {
      return planeElement.element.children.filter((child: Base) => child.type === type)[0];
    }
    return undefined;
  }

  public static getChildConnectionById(element: Base, id: string,elementRegistry:any): Connection {
    if(element.children && element.children.length > 0) {
      return element.children.filter((child: Base) => child instanceof Connection && child.businessObject.id === id)[0];
    }
    const planeElement = elementRegistry._elements[element.id + "_plane"];
    if(planeElement) {
      return planeElement.element.children.filter((child: Base) => child instanceof Connection && child.businessObject.id === id)[0];
    }
    return undefined;
  }

  public static isBpmnDefaultElement(element: Shape): boolean {
    // if connection
   if(element.waypoints) {
     return (element.businessObject && element.businessObject.sourceRef && element.businessObject.sourceRef.$type === startEvent) ||
       (element.businessObject && element.businessObject.targetRef != null && element.businessObject.targetRef.$type === endEvent) ||
       element.parent && element.parent.type !== processType;
   }
      return element.businessObject && element.businessObject.$type === startEvent || element.type === endEvent || element.businessObject && element.businessObject.$type === endEvent ||
        (element.type !== processType && element.parent && element.parent.type !== processType);
  }

  public static isConnection(element): boolean {
    return element.waypoints !== undefined;
  }

  public static isFloatedConnection(element: Connection): boolean {
    return this.isConnection(element) && (this.connectionWithoutBounds(element) || (ModelingUtil.isGateway(element.source) && !element.target && element.source.incoming.length === 0) || (ModelingUtil.isGateway(element.target) && !element.source && element.target.outgoing.length === 0));
  }

  public static connectionWithoutBounds(connection: Connection): boolean {
    return !connection.source && !connection.target;
  }

  public static getExtension(element: Shape, type: string) {
    let extensionElements = null;
    if (element.businessObject) {
      extensionElements = element.businessObject.extensionElements;
    } else {
      extensionElements = element.extensionElements;
    }
    if (extensionElements && extensionElements.values) {
      return extensionElements.values.filter(function (e) {
        return e.$instanceOf(type);
      })[0];
    }
  }

  public static getNodeDetails(element: Shape): NodeDetails {
    return ModelingUtil.getExtension(element, nodesDetailsExtensionType);
  }

  public static getTrackingChange(element: Shape): TrackingChange {
    return ModelingUtil.getExtension(element, trackingChangeExtensionType);
  }

  public static getElementBO(element: Shape) {
    return element.businessObject ? element.businessObject : element;
  }


  public static getGuideBox(element: Shape): GuideBox {
    return ModelingUtil.getExtension(element, guideBoxPropertyExtensionType);
  }

  public static isGuideBoxEmpty(guideBox: GuideBox): boolean {
    return (isEmpty(guideBox) ||
      (isEmpty(guideBox.guidingText) && isEmpty(guideBox.hyperlinks) && isEmpty(guideBox.attachments))
    );
  }

  public static getNodeType(element): NodeType {
    const nodeDetails = ModelingUtil.getNodeDetails(element);
    if (nodeDetails) {
      return nodeDetails.nodeType;
    } else {
      return element.nodeType;
    }
  }
  public static isTreatmentNode(element: Shape): boolean {
    return ModelingUtil.isNodeOfType(element, NodeType.treatment);

  }

  public static isNeighborhoodEntryNode(element: Shape): boolean {
    return ModelingUtil.isNodeOfType(element, NodeType.neighborhood_entry);
  }

  public static isNeighborhoodHeadNode(element: Shape): boolean {
      return ModelingUtil.isNodeOfType(element, NodeType.neighborhood_head);
  }

  public static isHeadNode(element: Shape): boolean {
      return ModelingUtil.isNodeOfType(element, NodeType.head);
  }

  private static isNodeOfType(element: Shape, type: NodeType): boolean{
    return element ? ModelingUtil.getNodeType(element) === type : false;
  }


  public static getElementType(element): string {
    return element ? element.type : undefined;
  }

  public static isGateway(element): boolean {
    return element ? this.getElementType(element) === exclusiveGatewayType : false;
  }

  public static getShapeOutgoingConnections(shape: Shape): ConnectionModdleElement[] {
    return shape.businessObject.outgoing;
  }

  public static getSubProcessCounterVariable(node: Shape) {
    return generateCounterName(node);
  }

  public static getConnectionTarget(connection: Connection): NodeModdleElement {
    return connection.businessObject.targetRef || connection.target;
  }

  public static getPreviousStep(element: Shape, depth: number) {
    if (this.isGateway(element)) {
      element = get<Shape>(element, 'incoming[0].source', element);
    }
    if (depth === 0) {
      return element;
    } else {
      return this.getPreviousStep(get<Shape>(element, 'incoming[0].source', element), --depth);
    }
  }

  public static hasChildOfType(element: Base, type: NodeType): boolean {
    return element.outgoing.some(connection => {
      if (this.getNodeType(connection.target) !== undefined) {
        return type === this.getNodeType(connection.target);
      } else {
        return this.hasChildOfType(connection.target, type);
      }
    });
  }

  /**
   * returns true if the element has at least one none deleted node(it excludes gateways)
   * @param {} element
   * @returns {boolean}
   */
  public static hasActiveChild(element: Base): boolean {
    return element.outgoing.find(connection => {
      if (this.getNodeType(connection.target) !== undefined) {
        const trackingChange = this.getTrackingChange(connection.target);
        return trackingChange ? trackingChange.type !== 'deleted' : true;
      } else {
        return this.hasActiveChild(connection.target);
      }
    });
  }

  /**
   * Method Purpose : Check if all childs has a given Tracking change
   * @param element :  parent element
   * @param tr : Tracking change
   */
  public static AllChildsWithTrackingChange(element: Base, tr: string): boolean {
    return element.outgoing.every(connection => {
      const trackingChange = this.getTrackingChange(connection.target);
      return trackingChange ? trackingChange.type === tr : false;
    });
  }

  public static AllChildsWithDeletedTrackingChange(element: Base): boolean {
    return this.AllChildsWithTrackingChange(element, 'deleted');
  }

  /**
   * Method Purpose : Check if all outgoing has a given Tracking change
   * @param element :  parent element
   * @param tr : Tracking change
   */

  public static AllOutgoingConnectionWithTrackingChange(element: Base, tr: string): boolean {
    return element.outgoing.every(connection => {
      const trackingChange = this.getTrackingChange(connection);
      return trackingChange ? trackingChange.type === tr : false;
    });
  }

  public static AllOutgoingConnectionWithDeletedTrackingChange(element: Base): boolean {
    return this.AllOutgoingConnectionWithTrackingChange(element, 'deleted');
  }

  public static hasOutgoingConnectionWithDeletedTrackingChange(element: Base): boolean {
    return this.hasOutgoingConnectionWithTrackingChange(element, 'deleted');
  }

  public static allOutgoingConnectionWithDeletedTrackingChange(element: Base): boolean {
    return this.allOutgoingConnectionWithTrackingChange(element, 'deleted');
  }

  /**
   * Method Purpose : Check if some outgoing has a given Tracking change
   * @param element :  parent element
   * @param tr : Tracking change
   */

  public static hasOutgoingConnectionWithTrackingChange(element: Base, tr: string): boolean {
    return element.outgoing.some(connection => {
      const trackingChange = this.getTrackingChange(connection);
      return trackingChange ? trackingChange.type === tr : false;
    });
  }

  /**
   * Method Purpose : Check if all outgoing has a given Tracking change
   * @param element :  parent element
   * @param tr : Tracking change
   */

  public static allOutgoingConnectionWithTrackingChange(element: Base, tr: string): boolean {
    return element.outgoing.every(connection => {
      const trackingChange = this.getTrackingChange(connection);
      return trackingChange ? trackingChange.type === tr : false;
    });
  }

  public static allOutGoingConnectionsToChildOfTypeHasSameTrackingChange(element: Base, nodeType: NodeType, tr: string): boolean {
    return element.outgoing.every(connection => {
      if (nodeType === ModelingUtil.getNodeType(connection.target)) {
        const connectionTrackingChange = this.getTrackingChange(connection);
        const targetTrackingChange = this.getTrackingChange(connection.target);
        return connectionTrackingChange ? connectionTrackingChange.type === tr : targetTrackingChange ? targetTrackingChange.type === tr : false;
      } else {
        return true;
      }
    });
  }

  /**
   * Method Purpose : Iterate over childs of a given node type, validate that every child has the given tracking change
   * @param element :  parent element
   * @param nodeType : node type for filtering
   * @param tr : Tracking change
   */
  public static isAllChildsOfGivenNodeTypeHasTheSameTR(element: Base, nodeType: NodeType, tr: string): boolean {
    let childs = element.outgoing.filter(connection => nodeType === ModelingUtil.getNodeType(connection.target))
    if (childs.length == 0) {
      return false;
    }
    return childs.every(child => {
      const trackingChange = this.getTrackingChange(child.target);
      return trackingChange ? trackingChange.type === tr : false;
    });
  }

  public static hasChildren(element: Base): boolean {
    return element.outgoing.length > 0 && !this.allOutgoingConnectionWithDeletedTrackingChange(element);
  }

  public static hasOutgoingGateway(element: Base): boolean {
    if (this.hasChildren(element)) {
      const outgoingConnection = element.outgoing[0];
      if (this.isGateway(outgoingConnection.target)) {
        return true;
      }
    }
    return false;
  }

  public static getParentGateways(element: Base): Array<Shape> {
    if (ModelingUtil.getNodeIncomingConnections(element).length === 0) {
      return [];
    } else {
      const parentConnections: Array<Shape> = [];
      // get all gateways
      ModelingUtil.getNodeIncomingConnections(element).forEach(connection => parentConnections.push(connection.source));
      return parentConnections;
    }
  }

  public static hasSimpleLoops(element: Shape): boolean {
    if (ModelingUtil.getNodeIncomingConnections(element) && ModelingUtil.getNodeIncomingConnections(element).length > 0 && ModelingUtil.getNodeOutgoingConnections(element) && ModelingUtil.getNodeOutgoingConnections(element).length > 0) {
      return ModelingUtil.getNodeIncomingConnections(element).some(incomConn => element.outgoing.some(outcomConn => incomConn.source.id === outcomConn.target.id));
    }
    return false;
  }

  public static isPlacedOnSubtreePage(element: Shape): boolean {
    const ancestors: Array<Shape> = ModelingUtil.getNodeAncestors(element);
    if (ancestors.length === 0) {
      return true;
    } else {
      return ancestors.some(ancs => ModelingUtil.containsNodeOfType(ancs, NodeType.neighborhood_entry));
    }
  }

  public static getElementsFromArray(elements) {
    let elementsToReturn = [];
    const length = elements.length;
    if (length === 1) {
      return elements[0]
    } else {
      for (let i = 0; i < length; i++) {
        elementsToReturn = [...elementsToReturn, elements[i]];
      }
      return elementsToReturn;
    }
  }

  public static isNode(element: Shape) {
    return is(element, subProcessType) || is(element, taskType);
  }

  public static getDescendentNodesAtCurrentLayer(element: Shape) {
    let nodesAtCurrentLevel: Array<Shape> = [];
    let collectedNodes: Array<Shape> = [];
    if (this.isNode(element)) {
      nodesAtCurrentLevel.push(element);
    }
    while (!isEmpty(nodesAtCurrentLevel)) {
      const currentNode = nodesAtCurrentLevel.pop();
      if (!collectedNodes.includes(currentNode)) {
        const nodeType = ModelingUtil.getNodeType(currentNode);
        if (this.isNode(currentNode)) {
          collectedNodes.push(currentNode);
        }
        if (nodeType !== NodeType.treatment && nodeType !== NodeType.neighborhood_entry) {
          const outgoingConnections = currentNode.outgoing;
          for (const conn of outgoingConnections) {
            nodesAtCurrentLevel.push(conn.target);
          }
        }
      }
    }
    return collectedNodes;
  }

  public static mergeArrays(array1: Array<any>, array2: Array<any>) {
    if (array1.length === 0) {
      return [...array2];
    }
    if (array2.length === 0) {
      return [array1];
    } else {
      return [array1, ...array2];
    }
  }

  public static getParentsForConnectionSource(source) {
    let parentsToReturn;
    if (this.getNodeType(source) === NodeType.head || this.getNodeType(source) === NodeType.neighborhood_head) {
      parentsToReturn = [source];
    } else {
      const parentNodes = ModelingUtil.getNodeParents(source);
      const parents = [source, ...parentNodes];
      parentsToReturn = parents.filter((parent) => parent !== undefined);
    }

    return parentsToReturn;
  }

  public static getConnection(source: Shape, target: Shape) {

    const connection = source.outgoing.find((outgoingCon) => target.incoming.find((incomingCon) => incomingCon.id === outgoingCon.id));
    return connection;
  }

  public static hasDeletionTrackingChange(element) {
    const trackingChange = ModelingUtil.getTrackingChange(element);
    return trackingChange ? trackingChange.type === 'deleted' : false;
  }

  public static connectionIsStraight(element) {
    return element ? element.waypoints.length === 2 : false;
  }

  public static getConnectionWithDeletionTrackingChange(source: Shape, target: Shape): Connection {
    const sourceOutgoingConnections = this.getNodeOutgoingConnections(source, true);
    const connection = sourceOutgoingConnections.find((outgoingConnection) => outgoingConnection.target && outgoingConnection.target.id === target.id);

    return connection && this.hasDeletionTrackingChange(connection) ? connection : undefined;
  }

  public static isConnectionToPreviousLayer(source: Shape, target: Shape): boolean {
    if (this.getNodeType(target) !== NodeType.treatment) {
      const sourceAncestors = this.getNodeAncestors(source);
      const targetAncestors = this.getNodeAncestors(target);
      return sourceAncestors && targetAncestors && sourceAncestors.length && targetAncestors.length ? targetAncestors.length < sourceAncestors.length : false;
    }
    return false;
  }

  private static getNodeAncestorsRecursion(element: Shape, visited: Array<string>): Array<Shape> {
    let shapes = [];
    const incomingConnections = ModelingUtil.getNodeIncomingConnections(element);
    let source;
    let foundVisited = false;
    for (const conn of incomingConnections) {
      source = conn.source;
      if (visited.find(x => x === source.id)) {
        foundVisited = true;
      } else {
        if (this.isNode(source)) {
          visited.push(source.id);
          shapes.push(source);
        }
      }
    }
    if (incomingConnections.length > 0 && !foundVisited) {
      shapes = ModelingUtil.mergeArrays(shapes, this.getNodeAncestorsRecursion(source, visited));
    }
    return shapes;
  }

  private static containsNodeOfType(shapes, type: NodeType): boolean {
    return shapes.some(shape => ModelingUtil.getNodeType(shape) == type);
  }

  private static getNodeDescendantsRecursive(shape: Shape, visited: Array<Shape>, filter: (element) => boolean) {
    let shapes = [];
    const outgoingConnections = shape.outgoing;
    let target;
    let foundVisited = false;
    for (const conn of outgoingConnections) {
      target = conn.target;
      if (visited.find(x => x === target.id)) {
        foundVisited = true;
      } else {
        if (this.isNode(target)) {
          visited.push(target.id);
          if (filter(target)) {
            shapes.push(target);
          }
        }
      }
    }
    if (outgoingConnections.length > 0 && !foundVisited) {
      shapes = ModelingUtil.mergeArrays(shapes, this.getNodeDescendantsRecursive(target, visited, filter));
    }
    return shapes;
  }

  private static getPathsToRoot(element: Shape, currentPath: Array<Shape>, paths) {
    const incomingConnections = element.incoming;
    if (incomingConnections.length === 0 && currentPath.length > 0) {
      paths.push(currentPath);
    }
    for (const conn of incomingConnections) {
      const source = conn.source;
      if (this.isNode(source)) {
        this.getPathsToRoot(source, [...currentPath, source], paths);
      } else {
        this.getPathsToRoot(source, [...currentPath], paths);
      }
    }
  }

  public static getVerticalOverflow(element:HTMLElement, bottomOffset: number): OverflowDirection {
    const bounding = element.getBoundingClientRect();
    if (bounding.bottom + bottomOffset > (window.innerHeight || document.documentElement.clientHeight)) {
      return OverflowDirection.Bottom;
    }

    const svgElement = this.findParentSvgElement(element);
    if (!svgElement) {
      return OverflowDirection.None;
    }

    if (bounding.top - svgElement.getBoundingClientRect().top - bottomOffset < 0) {
      return OverflowDirection.Top;
    }

    return OverflowDirection.None;
  }

  private static findParentSvgElement(element: HTMLElement): HTMLElement {
    while (element && element.nodeName !== "svg") {
      element = <HTMLElement> element.parentNode;
    }
    return element;
  }

  public static nodeIsFloated(element): boolean{
    const diElementIncoming = element.di && element.di.bpmnElement.incoming && element.di.bpmnElement.incoming.length ==0;
    const diElementOutgoing = element.di && element.di.bpmnElement.outgoing && element.di.bpmnElement.outgoing.length ==0;
    const elementWithBO = element.businessObject ? element.businessObject : element;
    return isEmpty(elementWithBO.incoming)  && isEmpty(elementWithBO.outgoing) || (diElementIncoming && diElementOutgoing);
  }
  public static hasConnectionToEndNode(element): boolean{
    return element.outgoing.length === 0 ? false : this.getConnectionTarget(element.outgoing[0]).id === END_NODE_ID;
  }

  public static getSourcesByTypeFromTargets(type: string, targets: Array<Shape>, sourcesToReturn: Set<Shape>) {
      if (!isEmpty(targets)) {
          targets.forEach((node) => {
              const sources = node.incoming.filter((connection) => ModelingUtil.getElementType(connection.source) === type).map((connection) => connection.source);
              sources.forEach((source) => sourcesToReturn.add(source));
              this.getSourcesByTypeFromTargets(type, sources, sourcesToReturn);
          });
      }
  }

  public static getNeighborhoodHeadByEntry(element: Shape){
    return element.outgoing ? element.outgoing[0].target : undefined;
  }

  public static isFloatedGateway(element) {
    return this.isGateway(element) && this.nodeIsFloated(element);
  }
}


